import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbOverrides } from '../../layout/navigation/BreadcrumbOverridesContext';
import { analytics } from '../../services/analytics';
import { useGetTeamQuery } from '../../store/api/apiSlice';
import { selectEffectivePersonId } from '../../store/auth/selectors';
import { useAppSelector } from '../../store/hooks';
import { formatCompactArtifactBadgeDate } from '../coach-artifacts/utils/artifactHeaderDate';
import { getArtifactTypeLabel } from '../coach-artifacts/utils/artifactLabels';
import {
  getArtifactGenerationActionabilityMessage,
  getArtifactGenerationBlockedMessage,
  getArtifactGenerationReadinessMessage,
} from '../coach-artifacts/utils/generationBlockedMessages';
import { PROFILE_SLUGS } from '../coach-artifacts/utils/profileSlugs';
import { getSessionTypeLabel } from '../coach-artifacts/utils/sessionDisplay';
import {
  type SessionArtifactSummary,
  type SessionGenerationJobStatus,
  type SessionGenerationStatusResponse,
  type SessionRow,
  useGenerateSessionArtifactArcMutation,
  useGenerateSessionArtifactMutation,
  useGetSessionArtifactArcGenerationStatusQuery,
  useGetSessionArtifactGenerationStatusQuery,
  useLazyGetSessionArtifactGenerationStatusQuery,
  useLazyListSessionsQuery,
  useListSessionsQuery,
} from './api/sessionApi';
import type { CoachSessionTranscriptLocationState } from './CoachSessionTranscriptPage';
import { SelectionActionBar } from './components';
import { getMemberDisplayName } from './types';
import { formatSessionTimeRange } from './utils/formatSessionTimeRange';
import { isDelayedAutomaticGenerationJob } from './utils/generationJobStatus';

const SESSION_PAGE_SIZE = 50;
const GENERATION_STATUS_POLL_INTERVAL_MS = 3000;
const COACH_MEMBER_SESSION_ROLE = 'coachee';
const LEGACY_ARTIFACT_TITLE_DATE_SUFFIX_PATTERN =
  / - \d{4}\.\d{2}\.\d{2}(?:\/\d{4}\.\d{2}\.\d{2})?$/;

function formatSessionDate(value?: string): string {
  if (!value) {
    return 'Session date unavailable';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Session date unavailable';
  }

  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getErrorMessage(
  error: FetchBaseQueryError | SerializedError | Error | string | null | undefined,
  fallback: string
): string | null {
  if (!error) {
    return null;
  }

  if (typeof error === 'string' && error.trim() !== '') {
    return error;
  }

  if (typeof error === 'object' && error !== null && 'status' in error) {
    if (typeof error.data === 'object' && error.data !== null) {
      const data = error.data as {
        error_description?: unknown;
        message?: unknown;
      };
      if (typeof data.error_description === 'string' && data.error_description.trim() !== '') {
        return data.error_description;
      }
      if (typeof data.message === 'string' && data.message.trim() !== '') {
        return data.message;
      }
    }

    if ('error' in error && typeof error.error === 'string' && error.error.trim() !== '') {
      return error.error;
    }

    return fallback;
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return fallback;
}

function mergeSessions(existing: SessionRow[], incoming: SessionRow[]): SessionRow[] {
  const byID = new Map(existing.map((session) => [session.session_id, session]));
  for (const session of incoming) {
    byID.set(session.session_id, session);
  }
  return Array.from(byID.values());
}

function getResolvedOrganizerCoachID(session: SessionRow): string | null {
  const coach = session.participants.find(
    (participant) =>
      participant.role === 'organizer_coach' &&
      participant.resolution_status === 'resolved' &&
      participant.person_id?.trim()
  );
  return coach?.person_id?.trim() || null;
}

function hasUsableTranscriptInput(session: SessionRow): boolean {
  return (
    session.attachments?.some(
      (attachment) =>
        attachment.kind === 'transcript' &&
        attachment.status === 'accepted' &&
        attachment.content_status === 'ready' &&
        attachment.drive_file_id.trim() !== ''
    ) ?? false
  );
}

function getGenerationAction(session: SessionRow, profileSlug: string) {
  return session.artifact_generation_actions?.find((action) => action.profile_slug === profileSlug);
}

function getGenerationUnavailableReason(session: SessionRow, profileSlug: string): string | null {
  const action = getGenerationAction(session, profileSlug);
  if (action?.status === 'available') {
    return null;
  }
  return getArtifactGenerationActionabilityMessage(action?.reason);
}

function canGenerateProfile(session: SessionRow, profileSlug: string): boolean {
  return getGenerationUnavailableReason(session, profileSlug) === null;
}

function isChemCallSession(session: SessionRow): boolean {
  return session.session_type === 'chem_call';
}

export function canAddSessionToArtifactSelection({
  candidateIsChemCall,
  selectedCount,
  selectionContainsChemCall,
}: {
  candidateIsChemCall: boolean;
  selectedCount: number;
  selectionContainsChemCall: boolean;
}): boolean {
  if (selectedCount === 0) {
    return true;
  }
  if (selectionContainsChemCall) {
    return false;
  }
  return !candidateIsChemCall;
}

function getSingleSessionFeedbackProfileSlug(session: SessionRow): string {
  return isChemCallSession(session)
    ? PROFILE_SLUGS.CHEM_CALL_REFLECTION
    : PROFILE_SLUGS.COACHING_FEEDBACK;
}

function getSelectedFeedbackProfileSlug(sessions: SessionRow[]): string {
  if (sessions.length > 1) {
    return PROFILE_SLUGS.COACHING_FEEDBACK_ARC;
  }
  const [session] = sessions;
  return session ? getSingleSessionFeedbackProfileSlug(session) : PROFILE_SLUGS.COACHING_FEEDBACK;
}

function getFeedbackMode(profileSlug: string): 'chem-call' | 'coaching' {
  return profileSlug === PROFILE_SLUGS.CHEM_CALL_REFLECTION ? 'chem-call' : 'coaching';
}

function getAnyGenerationUnavailableReason(session: SessionRow): string | null {
  const feedbackProfileSlug = getSingleSessionFeedbackProfileSlug(session);
  if (
    canGenerateProfile(session, feedbackProfileSlug) ||
    canGenerateProfile(session, PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY)
  ) {
    return null;
  }
  return (
    getGenerationUnavailableReason(session, feedbackProfileSlug) ??
    getGenerationUnavailableReason(session, PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY)
  );
}

function getSelectedProfileUnavailableReason(
  sessions: SessionRow[],
  profileSlug: string
): string | null {
  for (const session of sessions) {
    const reason = getGenerationUnavailableReason(session, profileSlug);
    if (reason) {
      return reason;
    }
  }
  return null;
}

function getArcInputIneligibleReason(session: SessionRow): string | null {
  if (session.artifact_generation_readiness?.status !== 'ready') {
    return getArtifactGenerationReadinessMessage(session.artifact_generation_readiness?.reason);
  }
  if (!hasUsableTranscriptInput(session)) {
    return 'Session needs an accepted transcript before arc generation.';
  }
  if (!getResolvedOrganizerCoachID(session)) {
    return 'Session needs a resolved organizer coach before arc generation.';
  }
  return null;
}

function getSingleSelectedOrganizerCoachID(
  sessionsByID: Map<string, SessionRow>,
  selectedSessionIds: Set<string>
): string | null {
  const organizerCoachIDs = new Set<string>();
  for (const sessionID of selectedSessionIds) {
    const session = sessionsByID.get(sessionID);
    if (!session || getArcInputIneligibleReason(session)) {
      continue;
    }
    const organizerCoachID = getResolvedOrganizerCoachID(session);
    if (organizerCoachID) {
      organizerCoachIDs.add(organizerCoachID);
    }
  }
  return organizerCoachIDs.size === 1 ? Array.from(organizerCoachIDs)[0] : null;
}

function doesSelectionContainChemCall(
  sessionsByID: Map<string, SessionRow>,
  selectedSessionIds: Set<string>
): boolean {
  return Array.from(selectedSessionIds).some((selectedSessionId) => {
    const selectedSession = sessionsByID.get(selectedSessionId);
    return selectedSession ? isChemCallSession(selectedSession) : false;
  });
}

function getArcSelectionDisabledReason({
  selected,
  selectedCount,
  selectedOrganizerCoachID,
  selectionContainsChemCall,
  session,
}: {
  selected: boolean;
  selectedCount: number;
  selectedOrganizerCoachID: string | null;
  selectionContainsChemCall: boolean;
  session: SessionRow;
}): string | null {
  if (!selected) {
    const generationUnavailableReason = getAnyGenerationUnavailableReason(session);
    if (generationUnavailableReason) {
      return generationUnavailableReason;
    }
    if (
      !canAddSessionToArtifactSelection({
        candidateIsChemCall: isChemCallSession(session),
        selectedCount,
        selectionContainsChemCall,
      })
    ) {
      return 'Chem Call sessions cannot be combined with other sessions.';
    }
  }
  const ineligibleReason = getArcInputIneligibleReason(session);
  if (ineligibleReason) {
    return ineligibleReason;
  }
  const organizerCoachID = getResolvedOrganizerCoachID(session);
  if (!selected && selectedOrganizerCoachID && organizerCoachID !== selectedOrganizerCoachID) {
    return 'Arc sessions must share the same organizer coach.';
  }
  return null;
}

function getCurrentArtifactID(
  session: SessionRow,
  profileSlug: string,
  status?: SessionGenerationStatusResponse
): string | null {
  const statusArtifactID = status?.current_artifact?.artifact_id;
  if (statusArtifactID) {
    return statusArtifactID;
  }

  return (
    session.artifacts?.find((artifact) => artifact.profile_slug === profileSlug)?.artifact_id ??
    null
  );
}

function artifactSessionIds(artifact: SessionArtifactSummary): string[] {
  return (artifact.session_ids ?? []).filter((sessionId) => sessionId.trim() !== '');
}

function matchesSessionSelection(
  artifact: SessionArtifactSummary,
  selectedSessionIds: Set<string>
): boolean {
  const artifactSessionIdSet = new Set(artifactSessionIds(artifact));
  if (artifactSessionIdSet.size !== selectedSessionIds.size) {
    return false;
  }
  return Array.from(selectedSessionIds).every((sessionId) => artifactSessionIdSet.has(sessionId));
}

function hasExistingArtifactForSelection(sessions: SessionRow[], profileSlug: string): boolean {
  const selectedSessionIds = new Set(sessions.map((session) => session.session_id));
  return sessions.some((session) =>
    (session.artifacts ?? []).some(
      (artifact) =>
        artifact.profile_slug === profileSlug &&
        matchesSessionSelection(artifact, selectedSessionIds)
    )
  );
}

function artifactProfileLabel(profileSlug: string): string {
  switch (profileSlug) {
    case PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY:
      return 'Session Summary';
    case PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY:
      return 'Coaching Arc Summary';
    case PROFILE_SLUGS.COACHING_FEEDBACK:
      return 'Coaching Reflection';
    case PROFILE_SLUGS.COACHING_FEEDBACK_ARC:
      return 'Coaching Reflection';
    case PROFILE_SLUGS.CHEM_CALL_REFLECTION:
      return 'Chem Call Reflection';
    case PROFILE_SLUGS.MEMBER_REFLECTION:
      return 'Member Reflection';
    case PROFILE_SLUGS.MEMBER_REFLECTION_ARC:
      return 'Member Reflection Arc';
    case PROFILE_SLUGS.SESSION_PREP_COACH:
      return 'Session Prep';
    default:
      return profileSlug;
  }
}

// The session read lists only accepted transcript attachments, which are the
// ones the transcripts API returns.
function hasTranscriptAttachment(session: SessionRow): boolean {
  return Boolean(
    session.attachments?.some(
      (attachment) => attachment.kind === 'transcript' && attachment.drive_file_id.trim() !== ''
    )
  );
}

// Only a ready readiness confirms a transcript file records a conversation.
// Readiness uses the same classifier as the transcripts API's cut; every other
// reason either stops before reading content or found no conversation, so the
// page could be empty (MEN-5401).
function hasReadableTranscript(session: SessionRow): boolean {
  return (
    hasTranscriptAttachment(session) && session.artifact_generation_readiness?.status === 'ready'
  );
}

function transcriptPath(personId: string, sessionId: string): string {
  return `/members/${encodeURIComponent(personId)}/sessions/${encodeURIComponent(sessionId)}/transcript`;
}

function artifactDisplayLabel(artifact: SessionArtifactSummary): string {
  const title = artifact.title.trim();
  if (LEGACY_ARTIFACT_TITLE_DATE_SUFFIX_PATTERN.test(title)) {
    return title;
  }

  const badgeDate = formatCompactArtifactBadgeDate(
    artifact.session_starts_at,
    artifact.session_ids?.length
  );
  return badgeDate ? `${title} - ${badgeDate}` : title;
}

function shouldDisplayArtifactOnSession(
  artifact: SessionArtifactSummary,
  session: SessionRow
): boolean {
  return artifact.display_session_id === session.session_id;
}

function isActiveGenerationJobStatus(status?: string): boolean {
  return status === 'queued' || status === 'running';
}

function isSuccessfulGenerationJobStatus(status?: string): boolean {
  return status === 'completed' || status === 'succeeded';
}

function generationPath(personId: string | undefined, jobId: string): string {
  const query = `job_id=${encodeURIComponent(jobId)}`;
  if (!personId) {
    return `/artifacts/generating?${query}`;
  }
  return `/members/${encodeURIComponent(personId)}/generating?${query}`;
}

function isSameGenerationAttempt(
  left: SessionGenerationJobStatus,
  right: SessionGenerationJobStatus
): boolean {
  return (
    left.job_id === right.job_id &&
    left.retry_count === right.retry_count &&
    (left.last_attempted_at ?? '') === (right.last_attempted_at ?? '')
  );
}

function LoadingState(): ReactElement {
  return (
    <Paper
      sx={{
        border: '1px solid #D9E3DD',
        borderRadius: '4px',
        boxShadow: '0 18px 40px rgba(24, 44, 32, 0.08)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={220} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={64} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={64} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={64} />
      </Box>
    </Paper>
  );
}

function RelatedArtifactsList({
  hasTranscript,
  session,
  generatingProfileSlugs,
  newArtifactIds,
  onOpen,
}: {
  hasTranscript: boolean;
  session: SessionRow;
  generatingProfileSlugs: string[];
  newArtifactIds: Set<string>;
  onOpen: (artifactId: string) => void;
}): ReactElement {
  const allArtifacts = session.artifacts ?? [];
  const artifacts = allArtifacts.filter((artifact) =>
    shouldDisplayArtifactOnSession(artifact, session)
  );
  // Drop a "Generating" placeholder once its artifact has actually landed in the row.
  const presentProfileSlugs = new Set(allArtifacts.map((artifact) => artifact.profile_slug));
  const pendingProfileSlugs = generatingProfileSlugs.filter(
    (profileSlug) => !presentProfileSlugs.has(profileSlug)
  );

  if (!hasTranscript) {
    return (
      <Typography sx={{ color: '#6A746D', fontSize: 13 }}>
        Moments artifacts not available without transcript
      </Typography>
    );
  }

  if (artifacts.length === 0 && pendingProfileSlugs.length === 0) {
    return <Typography sx={{ color: '#7C857E', fontSize: 14 }}>No artifacts yet</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
      {artifacts.map((artifact) => (
        <Badge
          key={artifact.artifact_id}
          color="error"
          variant="dot"
          overlap="circular"
          invisible={!newArtifactIds.has(artifact.artifact_id)}
        >
          <Box
            component="button"
            onClick={() => onOpen(artifact.artifact_id)}
            sx={{
              alignItems: 'center',
              backgroundColor: 'sage.200',
              border: 'none',
              borderRadius: '100px',
              color: 'sage.600',
              cursor: 'pointer',
              display: 'inline-flex',
              justifyContent: 'center',
              px: '6px',
              py: '3px',
              '&:hover': {
                backgroundColor: 'sage.300',
              },
            }}
            type="button"
          >
            <Typography
              sx={{
                color: 'sage.600',
                fontFamily: 'Basis Grotesque',
                fontSize: '10px',
                fontWeight: 500,
                lineHeight: '14px',
              }}
            >
              {artifactDisplayLabel(artifact)}
            </Typography>
          </Box>
        </Badge>
      ))}
      {pendingProfileSlugs.map((profileSlug) => (
        <Box
          key={`generating-${profileSlug}`}
          sx={{
            alignItems: 'center',
            color: '#6A746D',
            display: 'flex',
            fontSize: 13,
            gap: 1,
            minHeight: 28,
          }}
        >
          <CircularProgress size={12} sx={{ color: '#7C857E' }} />
          Generating {artifactProfileLabel(profileSlug)}...
        </Box>
      ))}
    </Box>
  );
}

function SessionGenerationStatusWatcher({
  profileSlug,
  session,
  onArtifactReady,
  onGenerationSettled,
}: {
  profileSlug: string;
  session: SessionRow;
  onArtifactReady: (artifactId: string) => void;
  onGenerationSettled: (
    sessionId: string,
    profileSlug: string,
    job?: SessionGenerationJobStatus
  ) => void;
}): ReactElement | null {
  const { data: status } = useGetSessionArtifactGenerationStatusQuery(
    { sessionId: session.session_id, profile_slug: profileSlug },
    {
      pollingInterval: GENERATION_STATUS_POLL_INTERVAL_MS,
    }
  );
  const artifactID = getCurrentArtifactID(session, profileSlug, status);
  const completedArtifactID =
    artifactID ??
    (isSuccessfulGenerationJobStatus(status?.latest_job?.status)
      ? status?.latest_job?.artifact_id
      : undefined);

  useEffect(() => {
    if (completedArtifactID) {
      onArtifactReady(completedArtifactID);
      onGenerationSettled(session.session_id, profileSlug);
      return;
    }

    if (status?.profile_eligibility?.status === 'blocked') {
      onGenerationSettled(session.session_id, profileSlug);
      return;
    }

    if (status?.latest_job?.status === 'failed' || status?.latest_job?.status === 'blocked') {
      onGenerationSettled(session.session_id, profileSlug, status.latest_job);
    }
  }, [
    completedArtifactID,
    onArtifactReady,
    onGenerationSettled,
    profileSlug,
    session.session_id,
    status?.latest_job,
    status?.profile_eligibility?.status,
  ]);

  return null;
}

function CoachSessionRow({
  arcSelectionDisabledReason,
  generatingProfileSlugs,
  memberId,
  memberLabel,
  newArtifactIds,
  selected,
  session,
  unavailableReason,
  onOpenArtifact,
  onToggleSelected,
}: {
  arcSelectionDisabledReason: string | null;
  generatingProfileSlugs: string[];
  memberId: string;
  memberLabel: string;
  newArtifactIds: Set<string>;
  selected: boolean;
  session: SessionRow;
  unavailableReason: string | null;
  onOpenArtifact: (artifactId: string) => void;
  onToggleSelected: (sessionId: string) => void;
}): ReactElement {
  const hasTranscript = hasTranscriptAttachment(session);
  const showTranscriptLink = hasReadableTranscript(session);
  const transcriptLinkState: CoachSessionTranscriptLocationState = { memberLabel };
  const sessionTypeLabel = getSessionTypeLabel(session.session_type);
  const displayedUnavailableReason = hasTranscript ? unavailableReason : null;
  const timeRange = formatSessionTimeRange(session.planned_start_at, session.planned_end_at);

  return (
    <TableRow sx={{ '&:last-child td': { borderBottom: 'none' } }}>
      <TableCell sx={{ borderBottomColor: '#E3E8E4', pl: 2, width: 72 }}>
        <Checkbox
          checked={selected}
          disabled={Boolean(arcSelectionDisabledReason)}
          inputProps={{ 'aria-label': `Select ${formatSessionDate(session.planned_start_at)}` }}
          onChange={() => onToggleSelected(session.session_id)}
        />
      </TableCell>
      <TableCell sx={{ borderBottomColor: '#E3E8E4', py: 2.25, width: 520 }}>
        <Typography sx={{ color: '#2B2E2B', fontSize: 15, fontWeight: 700 }}>
          {formatSessionDate(session.planned_start_at)}
          {timeRange ? ` · ${timeRange}` : ''}
        </Typography>
        <Box
          sx={{ alignItems: 'center', color: '#6A746D', display: 'flex', fontSize: 13, gap: 0.75 }}
        >
          <Typography component="span" sx={{ color: '#6A746D', fontSize: 13 }}>
            {sessionTypeLabel}
          </Typography>
          <Typography component="span" sx={{ color: '#9AA49D', fontSize: 13 }}>
            •
          </Typography>
          {showTranscriptLink ? (
            <Box
              component={RouterLink}
              onClick={(event) => event.stopPropagation()}
              state={transcriptLinkState}
              to={transcriptPath(memberId, session.session_id)}
              sx={{
                color: '#44514A',
                fontSize: 13,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                '&:hover': { color: '#1F2A24' },
              }}
            >
              Transcript
            </Box>
          ) : (
            <Typography component="span" sx={{ color: '#6A746D', fontSize: 13 }}>
              {hasTranscript ? 'Transcript not available' : 'No Transcript Found'}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ borderBottomColor: '#E3E8E4', py: 2.25, pr: 3 }}>
        <RelatedArtifactsList
          generatingProfileSlugs={generatingProfileSlugs}
          hasTranscript={hasTranscript}
          newArtifactIds={newArtifactIds}
          onOpen={onOpenArtifact}
          session={session}
        />
        {displayedUnavailableReason ? (
          <Typography sx={{ color: '#8A5A5A', fontSize: 13, mt: 1 }}>
            {displayedUnavailableReason}
          </Typography>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export default function CoachSessionArtifactsPage(): ReactElement {
  const navigate = useNavigate();
  const { personId } = useParams<{ personId: string }>();
  const coachPersonId = useAppSelector(selectEffectivePersonId);
  const { setBreadcrumbOverrides } = useBreadcrumbOverrides();
  const trackedViewRef = useRef(false);
  const [olderPages, setOlderPages] = useState<{
    items: SessionRow[];
    nextCursor: string | null;
  } | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [generationErrorMessage, setGenerationErrorMessage] = useState<string | null>(null);
  const [generatingKeys, setGeneratingKeys] = useState<Set<string>>(new Set());
  const generatingKeysRef = useRef<Set<string>>(new Set());
  const ignoredSettlementJobs = useRef<Map<string, SessionGenerationJobStatus>>(new Map());
  const memberScopePersonIDRef = useRef<string | undefined>(undefined);
  const [arcStatusMessage, setArcStatusMessage] = useState<string | null>(null);
  const [arcJobId, setArcJobId] = useState<string | null>(null);
  const [arcGeneration, setArcGeneration] = useState<{
    profileSlug: string;
    targetSessionId: string | null;
  } | null>(null);
  // Ephemeral, in-memory only: artifact ids that just finished generating get a
  // notification dot until the coach clicks the chip. Intentionally not persisted —
  // it resets on reload (POC behavior).
  const [newArtifactIds, setNewArtifactIds] = useState<Set<string>>(new Set());
  const startingGenerationKeysRef = useRef<Set<string>>(new Set());
  const [generateSessionArtifact] = useGenerateSessionArtifactMutation();
  const [getSessionArtifactGenerationStatus] = useLazyGetSessionArtifactGenerationStatusQuery();
  const [generateSessionArtifactArc, { isLoading: isGeneratingArc }] =
    useGenerateSessionArtifactArcMutation();
  const { data: teamData } = useGetTeamQuery();
  const {
    data: sessionsData,
    error: sessionsError,
    isLoading: isLoadingSessions,
    refetch: refetchSessions,
  } = useListSessionsQuery(
    personId && coachPersonId
      ? {
          coach_person_id: coachPersonId,
          include_unresolved: true,
          include_upcoming: true,
          limit: SESSION_PAGE_SIZE,
          person_id: personId,
          role: COACH_MEMBER_SESSION_ROLE,
        }
      : undefined,
    { skip: !personId || !coachPersonId }
  );
  const [loadMoreSessions, { isFetching: isFetchingMoreSessions }] = useLazyListSessionsQuery();
  const { data: arcStatusData } = useGetSessionArtifactArcGenerationStatusQuery(
    { job_id: arcJobId ?? '' },
    {
      pollingInterval: arcJobId ? GENERATION_STATUS_POLL_INTERVAL_MS : 0,
      skip: !arcJobId,
    }
  );

  const member = useMemo(() => {
    if (!teamData?.members || !personId) return null;
    return teamData.members.find((teamMember) => teamMember.person.id === personId) || null;
  }, [personId, teamData?.members]);
  const memberName = member ? getMemberDisplayName(member) : 'Member';
  const sessions = useMemo(
    () => mergeSessions(sessionsData?.items ?? [], olderPages?.items ?? []),
    [olderPages, sessionsData?.items]
  );
  const sessionsByID = useMemo(
    () => new Map(sessions.map((session) => [session.session_id, session])),
    [sessions]
  );
  const selectedArcSessions = useMemo(
    () => sessions.filter((session) => selectedSessionIds.has(session.session_id)),
    [selectedSessionIds, sessions]
  );
  const selectedOrganizerCoachID = useMemo(
    () => getSingleSelectedOrganizerCoachID(sessionsByID, selectedSessionIds),
    [selectedSessionIds, sessionsByID]
  );
  const selectionContainsChemCall = useMemo(
    () => doesSelectionContainChemCall(sessionsByID, selectedSessionIds),
    [selectedSessionIds, sessionsByID]
  );
  const canGenerateSelectedArc = useMemo(() => {
    if (selectedArcSessions.length < 2) {
      return false;
    }
    const organizerCoachIDs = new Set<string>();
    for (const session of selectedArcSessions) {
      if (getArcInputIneligibleReason(session)) {
        return false;
      }
      const organizerCoachID = getResolvedOrganizerCoachID(session);
      if (organizerCoachID) {
        organizerCoachIDs.add(organizerCoachID);
      }
    }
    return organizerCoachIDs.size === 1;
  }, [selectedArcSessions]);
  const selectedFeedbackProfileSlug = getSelectedFeedbackProfileSlug(selectedArcSessions);
  const selectedSummaryProfileSlug =
    selectedArcSessions.length > 1
      ? PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY
      : PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY;
  const selectedFeedbackUnavailableReason = getSelectedProfileUnavailableReason(
    selectedArcSessions,
    selectedFeedbackProfileSlug
  );
  const selectedSummaryUnavailableReason = getSelectedProfileUnavailableReason(
    selectedArcSessions,
    selectedSummaryProfileSlug
  );
  const selectedArcUnavailableReason =
    selectedArcSessions.length > 1 && !canGenerateSelectedArc
      ? 'Selected sessions must be ready and share the same organizer coach.'
      : null;
  const showSelectedFeedbackAction =
    selectedArcSessions.length > 0 &&
    !hasExistingArtifactForSelection(selectedArcSessions, selectedFeedbackProfileSlug);
  const showSelectedSummaryAction =
    selectedArcSessions.length > 0 &&
    !hasExistingArtifactForSelection(selectedArcSessions, selectedSummaryProfileSlug);
  const activeGenerationTargets = useMemo(
    () =>
      Array.from(generatingKeys).flatMap((key) => {
        const separatorIndex = key.indexOf(':');
        if (separatorIndex < 0) {
          return [];
        }
        const sessionId = key.slice(0, separatorIndex);
        const profileSlug = key.slice(separatorIndex + 1);
        const session = sessionsByID.get(sessionId);
        return session ? [{ key, profileSlug, session }] : [];
      }),
    [generatingKeys, sessionsByID]
  );
  const generatingProfilesBySession = useMemo(() => {
    const bySession = new Map<string, string[]>();
    const add = (sessionId: string, profileSlug: string) => {
      const list = bySession.get(sessionId) ?? [];
      list.push(profileSlug);
      bySession.set(sessionId, list);
    };
    for (const { profileSlug, session } of activeGenerationTargets) {
      add(session.session_id, profileSlug);
    }
    // An in-flight arc job shows its placeholder on the session it will land on
    // (the most recent of the selected sessions).
    if (arcGeneration?.targetSessionId) {
      add(arcGeneration.targetSessionId, arcGeneration.profileSlug);
    }
    return bySession;
  }, [activeGenerationTargets, arcGeneration]);
  const selectedGenerationInFlight = useMemo(
    () =>
      activeGenerationTargets.some(({ session }) => selectedSessionIds.has(session.session_id)) ||
      isGeneratingArc ||
      Boolean(arcJobId),
    [activeGenerationTargets, arcJobId, isGeneratingArc, selectedSessionIds]
  );
  const selectionActionDisabled = selectedGenerationInFlight || selectedArcSessions.length === 0;
  const selectedFeedbackDisabled = Boolean(
    selectionActionDisabled || selectedFeedbackUnavailableReason || selectedArcUnavailableReason
  );
  const selectedSummaryDisabled = Boolean(
    selectionActionDisabled || selectedSummaryUnavailableReason || selectedArcUnavailableReason
  );
  const selectionStatusLabel =
    arcStatusMessage ??
    (selectedGenerationInFlight ? 'Generating selected session artifact.' : null);
  const nextCursor = olderPages ? olderPages.nextCursor : (sessionsData?.next_cursor ?? null);
  const sessionsErrorMessage = getErrorMessage(
    sessionsError,
    'Unable to load member sessions right now.'
  );
  const loadMoreSessionsErrorMessage = getErrorMessage(
    loadMoreError,
    'Unable to load older member sessions right now.'
  );

  useEffect(() => {
    if (memberScopePersonIDRef.current === personId) {
      return;
    }
    memberScopePersonIDRef.current = personId;
    setOlderPages(null);
    setLoadMoreError(null);
    setSelectedSessionIds(new Set());
    setGenerationErrorMessage(null);
    const emptyGeneratingKeys = new Set<string>();
    generatingKeysRef.current = emptyGeneratingKeys;
    setGeneratingKeys(emptyGeneratingKeys);
    startingGenerationKeysRef.current.clear();
    ignoredSettlementJobs.current.clear();
    setArcStatusMessage(null);
    setArcJobId(null);
    setArcGeneration(null);
    setNewArtifactIds(new Set());
    trackedViewRef.current = false;
  });

  useEffect(() => {
    if (!personId) return;
    setBreadcrumbOverrides({
      memberContext: {
        memberId: personId,
        memberLabel: memberName,
      },
    });
  }, [memberName, personId, setBreadcrumbOverrides]);

  useEffect(() => {
    if (trackedViewRef.current || !personId) return;
    analytics.viewed('Viewed member', 'member', `/members/${personId}`, {
      member_id: personId,
    });
    trackedViewRef.current = true;
  }, [personId]);

  // Drop accumulated older pages when the first-page query errors so we do not
  // render stale older pages alongside an error state.
  useEffect(() => {
    if (sessionsError) {
      setOlderPages(null);
      setLoadMoreError(null);
    }
  }, [sessionsError]);

  useEffect(() => {
    setSelectedSessionIds((current) => {
      let changed = false;
      const next = new Set<string>();
      for (const sessionID of current) {
        const session = sessionsByID.get(sessionID);
        if (
          session &&
          !getArcInputIneligibleReason(session) &&
          !getAnyGenerationUnavailableReason(session)
        ) {
          next.add(sessionID);
          continue;
        }
        changed = true;
      }
      return changed ? next : current;
    });
  }, [sessionsByID]);

  useEffect(() => {
    const job = arcStatusData?.job;
    if (!job) {
      return;
    }
    if (isActiveGenerationJobStatus(job.status)) {
      setArcStatusMessage(`${getArtifactTypeLabel(job.profile_slug)} generation is in progress.`);
      return;
    }
    if (isSuccessfulGenerationJobStatus(job.status) && job.artifact_id) {
      const artifactId = job.artifact_id;
      setArcStatusMessage(`${getArtifactTypeLabel(job.profile_slug)} generated.`);
      setArcJobId(null);
      setArcGeneration(null);
      setNewArtifactIds((current) => {
        const next = new Set(current);
        next.add(artifactId);
        return next;
      });
      void refetchSessions();
      return;
    }
    if (job.status === 'failed' || job.status === 'blocked') {
      setArcJobId(null);
      setArcGeneration(null);
      setArcStatusMessage(
        job.error_message || getArtifactGenerationBlockedMessage(job.blocked_reason)
      );
    }
  }, [arcStatusData?.job, refetchSessions]);

  const handleToggleSelected = useCallback(
    (sessionId: string) => {
      setSelectedSessionIds((current) => {
        const session = sessionsByID.get(sessionId);
        if (!session) {
          return current;
        }
        const next = new Set(current);
        if (next.has(sessionId)) {
          next.delete(sessionId);
          return next;
        }
        const disabledReason = getArcSelectionDisabledReason({
          selected: false,
          selectedCount: current.size,
          selectedOrganizerCoachID: getSingleSelectedOrganizerCoachID(sessionsByID, current),
          selectionContainsChemCall: doesSelectionContainChemCall(sessionsByID, current),
          session,
        });
        if (disabledReason) {
          return current;
        }
        next.add(sessionId);
        return next;
      });
    },
    [sessionsByID]
  );

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || !personId || !coachPersonId) {
      return;
    }

    setLoadMoreError(null);
    try {
      const page = await loadMoreSessions({
        coach_person_id: coachPersonId,
        cursor: nextCursor,
        include_upcoming: true,
        include_unresolved: true,
        limit: SESSION_PAGE_SIZE,
        person_id: personId,
        role: COACH_MEMBER_SESSION_ROLE,
      }).unwrap();

      setOlderPages((current) => ({
        items: mergeSessions(current?.items ?? [], page.items ?? []),
        nextCursor: page.next_cursor ?? null,
      }));
    } catch (error) {
      setLoadMoreError(
        getErrorMessage(
          error as FetchBaseQueryError | SerializedError | Error,
          'Unable to load older member sessions right now.'
        ) ?? 'Unable to load older member sessions right now.'
      );
    }
  }, [coachPersonId, loadMoreSessions, nextCursor, personId]);

  const handleOpenArtifact = useCallback(
    (artifactId: string) => {
      setNewArtifactIds((current) => {
        if (!current.has(artifactId)) {
          return current;
        }
        const next = new Set(current);
        next.delete(artifactId);
        return next;
      });
      navigate(`/artifacts/${encodeURIComponent(artifactId)}`);
    },
    [navigate]
  );

  // When a background generation finishes we no longer redirect the coach. Instead
  // we surface the finished artifact as a chip with a notification dot and refresh
  // the session list so the chip appears.
  const handleGeneratedArtifactReady = useCallback(
    (artifactId: string) => {
      setNewArtifactIds((current) => {
        if (current.has(artifactId)) {
          return current;
        }
        const next = new Set(current);
        next.add(artifactId);
        return next;
      });
      void refetchSessions();
    },
    [refetchSessions]
  );

  const clearGenerationKey = useCallback((key: string) => {
    const nextGeneratingKeys = new Set(generatingKeysRef.current);
    nextGeneratingKeys.delete(key);
    generatingKeysRef.current = nextGeneratingKeys;
    setGeneratingKeys(nextGeneratingKeys);
  }, []);

  const handleGenerate = useCallback(
    async (sessionId: string, profileSlug: string) => {
      const key = `${sessionId}:${profileSlug}`;
      if (generatingKeysRef.current.has(key) || startingGenerationKeysRef.current.has(key)) {
        return;
      }

      startingGenerationKeysRef.current.add(key);
      setGenerationErrorMessage(null);
      ignoredSettlementJobs.current.delete(key);

      try {
        const status = await getSessionArtifactGenerationStatus({
          sessionId,
          profile_slug: profileSlug,
        }).unwrap();

        if (status.current_artifact?.artifact_id) {
          navigate(`/artifacts/${encodeURIComponent(status.current_artifact.artifact_id)}`);
          return;
        }

        if (status.profile_eligibility?.status === 'blocked') {
          setGenerationErrorMessage(
            getArtifactGenerationBlockedMessage(status.profile_eligibility.reason) ||
              `Unable to start ${artifactProfileLabel(profileSlug)} generation right now.`
          );
          return;
        }

        if (status.latest_job) {
          // A running job is already in flight: watch it stream on the member-scoped
          // generating page so errors keep the coach in member context. A delayed
          // automatic queued job should fall through to the explicit generate call
          // below so the backend can promote it and clear generate_after.
          if (
            isActiveGenerationJobStatus(status.latest_job.status) &&
            !isDelayedAutomaticGenerationJob(status.latest_job)
          ) {
            navigate(generationPath(personId, status.latest_job.job_id));
            return;
          }
          // A finished job that produced an artifact: open it.
          if (
            isSuccessfulGenerationJobStatus(status.latest_job.status) &&
            status.latest_job.artifact_id
          ) {
            navigate(`/artifacts/${encodeURIComponent(status.latest_job.artifact_id)}`);
            return;
          }
          // Otherwise (failed, blocked, or completed without an artifact) fall through
          // and start/requeue a fresh run rather than dead-ending on a stale job.
          ignoredSettlementJobs.current.set(key, status.latest_job);
        }

        const nextGeneratingKeys = new Set(generatingKeysRef.current).add(key);
        generatingKeysRef.current = nextGeneratingKeys;
        setGeneratingKeys(nextGeneratingKeys);

        const generatedArtifact = await generateSessionArtifact({
          sessionId,
          profile_slug: profileSlug,
        }).unwrap();

        if (generatedArtifact.artifact_id) {
          navigate(`/artifacts/${encodeURIComponent(generatedArtifact.artifact_id)}`);
          return;
        }

        if (generatedArtifact.job_id) {
          navigate(generationPath(personId, generatedArtifact.job_id));
          return;
        }

        if (generatedArtifact.status === 'blocked' || generatedArtifact.status === 'failed') {
          setGenerationErrorMessage(
            generatedArtifact.error_description ||
              getArtifactGenerationBlockedMessage(generatedArtifact.blocked_reason) ||
              `Unable to start ${artifactProfileLabel(profileSlug)} generation right now.`
          );
          ignoredSettlementJobs.current.delete(key);
          clearGenerationKey(key);
        }

        if (generatedArtifact.accepted) {
          setGenerationErrorMessage(
            `${artifactProfileLabel(profileSlug)} generation did not return a job id.`
          );
          ignoredSettlementJobs.current.delete(key);
          clearGenerationKey(key);
        }
      } catch (error) {
        setGenerationErrorMessage(
          getErrorMessage(
            error as FetchBaseQueryError | SerializedError | Error,
            `Unable to start ${artifactProfileLabel(profileSlug)} generation right now.`
          ) ?? `Unable to start ${artifactProfileLabel(profileSlug)} generation right now.`
        );
        ignoredSettlementJobs.current.delete(key);
        clearGenerationKey(key);
      } finally {
        startingGenerationKeysRef.current.delete(key);
      }
    },
    [
      clearGenerationKey,
      generateSessionArtifact,
      getSessionArtifactGenerationStatus,
      navigate,
      personId,
    ]
  );

  const handleGenerationSettled = useCallback(
    (sessionId: string, profileSlug: string, job?: SessionGenerationJobStatus) => {
      const key = `${sessionId}:${profileSlug}`;
      const ignoredJob = ignoredSettlementJobs.current.get(key);
      if (job && ignoredJob && isSameGenerationAttempt(job, ignoredJob)) {
        return;
      }
      ignoredSettlementJobs.current.delete(key);
      if (!generatingKeysRef.current.has(key)) {
        return;
      }
      clearGenerationKey(key);
    },
    [clearGenerationKey]
  );

  const handleGenerateArc = useCallback(
    async (profileSlug: string) => {
      const selectedSessions = sessions.filter((session) =>
        selectedSessionIds.has(session.session_id)
      );
      if (selectedSessions.length < 2) {
        return;
      }

      setGenerationErrorMessage(null);
      setArcStatusMessage(null);
      try {
        const response = await generateSessionArtifactArc({
          profile_slug: profileSlug,
          session_ids: selectedSessions.map((session) => session.session_id),
        }).unwrap();

        if (response.artifact_id) {
          navigate(`/artifacts/${encodeURIComponent(response.artifact_id)}`);
          return;
        }
        if (response.job_id) {
          navigate(generationPath(personId, response.job_id));
          return;
        }
        if (response.blocked_reason || response.error_description) {
          setArcStatusMessage(
            response.error_description ||
              getArtifactGenerationBlockedMessage(response.blocked_reason)
          );
          return;
        }
        if (response.accepted) {
          setGenerationErrorMessage(
            `${artifactProfileLabel(profileSlug)} generation did not return a job id.`
          );
        }
      } catch (error) {
        setArcGeneration(null);
        setGenerationErrorMessage(
          getErrorMessage(
            error as FetchBaseQueryError | SerializedError | Error,
            `Unable to start ${artifactProfileLabel(profileSlug)} generation right now.`
          ) ?? `Unable to start ${artifactProfileLabel(profileSlug)} generation right now.`
        );
      }
    },
    [generateSessionArtifactArc, navigate, personId, selectedSessionIds, sessions]
  );

  const handleGenerateSelectedFeedback = useCallback(() => {
    if (selectedFeedbackDisabled) {
      setGenerationErrorMessage(
        selectedFeedbackUnavailableReason ||
          selectedArcUnavailableReason ||
          'Artifact generation is unavailable for this selection.'
      );
      return;
    }
    if (selectedArcSessions.length === 1) {
      void handleGenerate(selectedArcSessions[0].session_id, selectedFeedbackProfileSlug);
      return;
    }
    if (selectedArcSessions.length > 1) {
      void handleGenerateArc(selectedFeedbackProfileSlug);
    }
  }, [
    handleGenerate,
    handleGenerateArc,
    selectedArcSessions,
    selectedArcUnavailableReason,
    selectedFeedbackDisabled,
    selectedFeedbackProfileSlug,
    selectedFeedbackUnavailableReason,
  ]);

  const handleGenerateSelectedSummary = useCallback(() => {
    if (selectedSummaryDisabled) {
      setGenerationErrorMessage(
        selectedSummaryUnavailableReason ||
          selectedArcUnavailableReason ||
          'Artifact generation is unavailable for this selection.'
      );
      return;
    }
    if (selectedArcSessions.length === 1) {
      void handleGenerate(
        selectedArcSessions[0].session_id,
        PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY
      );
      return;
    }
    if (selectedArcSessions.length > 1) {
      void handleGenerateArc(PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY);
    }
  }, [
    handleGenerate,
    handleGenerateArc,
    selectedArcSessions,
    selectedArcUnavailableReason,
    selectedSummaryDisabled,
    selectedSummaryUnavailableReason,
  ]);
  const handleUnavailableSelectionAction = useCallback(() => undefined, []);

  return (
    <Box sx={{ bgcolor: 'sunny.100', minHeight: '100vh', width: '100%' }}>
      <Box sx={{ maxWidth: 1400, py: 1 }}>
        <Typography
          component="h1"
          sx={{
            color: 'cement.500',
            fontFamily: 'Basis Grotesque',
            fontSize: '32px',
            fontWeight: 700,
            lineHeight: 1.2,
            mb: 1,
          }}
          variant="h4"
        >
          {memberName}
        </Typography>
        <Typography
          sx={{
            color: 'cement.400',
            fontFamily: 'Basis Grotesque',
            fontSize: '16px',
            mb: 3,
          }}
          variant="body1"
        >
          Select sessions to summarize
        </Typography>

        {sessionsErrorMessage ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {sessionsErrorMessage}
          </Alert>
        ) : null}

        {loadMoreSessionsErrorMessage && !sessionsErrorMessage ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {loadMoreSessionsErrorMessage}
          </Alert>
        ) : null}

        {generationErrorMessage ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {generationErrorMessage}
          </Alert>
        ) : null}

        {activeGenerationTargets.map(({ key, profileSlug, session }) => (
          <SessionGenerationStatusWatcher
            key={key}
            onArtifactReady={handleGeneratedArtifactReady}
            onGenerationSettled={handleGenerationSettled}
            profileSlug={profileSlug}
            session={session}
          />
        ))}

        {isLoadingSessions ? <LoadingState /> : null}

        {!isLoadingSessions && !sessionsErrorMessage && sessions.length === 0 ? (
          <Paper
            sx={{
              border: '1px solid #D9E3DD',
              borderRadius: '4px',
              boxShadow: '0 18px 40px rgba(24, 44, 32, 0.08)',
              p: { xs: 3, md: 4 },
            }}
          >
            <Typography sx={{ color: '#2B3B33', fontSize: 24, fontWeight: 700, mb: 1 }}>
              No sessions yet
            </Typography>
            <Typography sx={{ color: '#566A60', fontSize: 16, lineHeight: 1.7 }}>
              Sessions will appear here once they are available in Moments.
            </Typography>
          </Paper>
        ) : null}

        {!isLoadingSessions && !sessionsErrorMessage && sessions.length > 0 ? (
          <>
            <Paper
              sx={{
                border: '1px solid #D9E3DD',
                borderRadius: '4px',
                boxShadow: '0 18px 40px rgba(24, 44, 32, 0.08)',
                overflow: 'hidden',
              }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FBFBFA' }}>
                    <TableCell sx={{ borderBottomColor: '#E3E8E4', py: 1.5, width: 72 }}>
                      <Typography
                        sx={{
                          color: '#5B655E',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Select
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: '#E3E8E4', py: 1.5, width: 520 }}>
                      <Typography
                        sx={{
                          color: '#5B655E',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Sessions
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottomColor: '#E3E8E4', py: 1.5, pr: 3 }}>
                      <Typography
                        sx={{
                          color: '#5B655E',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Related Artifacts
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <CoachSessionRow
                      key={session.session_id}
                      arcSelectionDisabledReason={getArcSelectionDisabledReason({
                        selected: selectedSessionIds.has(session.session_id),
                        selectedCount: selectedSessionIds.size,
                        selectedOrganizerCoachID,
                        selectionContainsChemCall,
                        session,
                      })}
                      generatingProfileSlugs={
                        generatingProfilesBySession.get(session.session_id) ?? []
                      }
                      memberId={personId ?? ''}
                      memberLabel={memberName}
                      newArtifactIds={newArtifactIds}
                      onOpenArtifact={handleOpenArtifact}
                      onToggleSelected={handleToggleSelected}
                      selected={selectedSessionIds.has(session.session_id)}
                      session={session}
                      unavailableReason={getAnyGenerationUnavailableReason(session)}
                    />
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <SelectionActionBar
              disabled={selectionActionDisabled}
              feedbackLabel={artifactProfileLabel(selectedFeedbackProfileSlug)}
              feedbackDisabled={selectedFeedbackDisabled}
              feedbackDisabledReason={
                selectedFeedbackUnavailableReason || selectedArcUnavailableReason
              }
              feedbackMode={getFeedbackMode(selectedFeedbackProfileSlug)}
              onGenerateFeedback={handleGenerateSelectedFeedback}
              onGenerateMilestonesAndWins={handleUnavailableSelectionAction}
              onGenerateSessionPrep={handleUnavailableSelectionAction}
              onGenerateSummary={handleGenerateSelectedSummary}
              selectedCount={selectedSessionIds.size}
              selectedItemLabel="Session"
              showFeedbackAction={showSelectedFeedbackAction}
              showSummaryAction={showSelectedSummaryAction}
              summaryDisabled={selectedSummaryDisabled}
              summaryDisabledReason={
                selectedSummaryUnavailableReason || selectedArcUnavailableReason
              }
              statusLabel={selectionStatusLabel}
            />

            {nextCursor ? (
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, mt: 2 }}>
                <Typography sx={{ color: '#6A746D', fontSize: 13 }}>
                  Showing {sessions.length} Sessions
                </Typography>
                <Button
                  disabled={isFetchingMoreSessions}
                  onClick={() => {
                    void handleLoadMore();
                  }}
                  sx={{ minWidth: 180 }}
                  variant="outlined"
                >
                  {isFetchingMoreSessions ? 'Loading older sessions...' : 'Load older sessions'}
                </Button>
              </Box>
            ) : (
              <Typography sx={{ color: '#6A746D', fontSize: 13, mt: 2 }}>
                Showing {sessions.length} of {sessions.length} Sessions
              </Typography>
            )}
          </>
        ) : null}
      </Box>
    </Box>
  );
}
